import React, { useState, useEffect } from 'react';
import styles from '../styles/Orders.module.css';
import Modal from '@mui/material/Modal';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { noop } from 'chart.js/helpers';
import { useAuth } from '../context/AuthContext';
import { getData, postData, putData, postFormData, deleteData } from '../ServerRequest.js';

// Helper to map API order to table row
const mapOrder = (order) => {
  // Use first item for product info (can be extended for multiple items)
  const firstItem = order.items && order.items.length > 0 ? order.items[0] : null;
  return {
    id: order.id,
    orderNumber: order.company_order_number || order.order_number,
    customerName: order.customer?.name || '-',
    customerPhoneNo: order.shipping_address?.mobile_number || order.billing_address?.mobile_number || '-',
    address:
      order.shipping_address
        ? `${order.shipping_address.address_line_1 || ''}, ${order.shipping_address.city || ''}, ${order.shipping_address.state || ''}`
        : '-',
    paymentConfirmed: order.status === 'confirmed' || order.status === 'delivered',
    status: order.status,
    trackingNumber: order.tracking_number || '-',
    productId: firstItem?.product?.id || '-',
    productName: firstItem?.product?.name || '-',
    price: firstItem?.product?.price || order.total_amount || '-',
    items: order.items || [],
    raw: order, // keep full order for modal
  };
};

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'dispatched', label: 'Order Dispatched' },
  { value: 'delivered', label: 'Order Delivered' },
  { value: 'cancelled', label: 'Order Cancelled' },
  { value: 'returned', label: 'Order Returned' },
];

const statusColors = {
  new: '#3B82F6',
  dispatched: '#F59E0B',
  delivered: '#10B981',
  cancelled: '#EF4444',
  returned: '#8B5CF6',
  confirmed: '#34D399',
  pending: '#FBBF24',
  shipped: '#8B5CF6'
};

const productPriceMap = {
  '014': 100,
  '013': 200,
  '012': 300,
  '011': 150,
  '015': 0,
  '016': 0,
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { getToken, getCompanyId } = useAuth();

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getToken();
        const companyId = getCompanyId();
        // getData should return the parsed JSON directly, or throw on error
        let data;
        try {
          data = await getData(`/api/order/orders/?company_id=${companyId}`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
              'Content-Type': 'application/json',
            },
          });
        } catch (apiErr) {
          setError('API error: ' + (apiErr.message || apiErr.toString()));
          setOrders([]);
          return;
        }
        // Fix: Use data.data.results as per your API response
        const results = data && data.data && Array.isArray(data.data.results) ? data.data.results : [];
        if (!results.length) {
          setError('API returned no results. Raw response: ' + JSON.stringify(data).slice(0, 200));
          setOrders([]);
          return;
        }
        setOrders(results.map(mapOrder));
      } catch (err) {
        setError('JS error: ' + (err.message || err.toString()));
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
    // eslint-disable-next-line
  }, []);

  const handleStatusChange = (idx, newStatus) => {
    setOrders(orders =>
      orders.map((order, i) =>
        i === idx
          ? {
            ...order,
            status: newStatus,
            trackingNumber:
              newStatus === 'dispatched' && !order.trackingNumber
                ? `TRK${Math.floor(Math.random() * 1000000)}`
                : newStatus !== 'dispatched'
                  ? ''
                  : order.trackingNumber,
          }
          : order
      )
    );
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
  };

  return (
    <div className={styles.ordersPage}>
      <h2 className={styles.ordersTitle}>
        Orders ({orders.length})
      </h2>
      {loading ? (
        <div className={styles.spinnerWrapper}>
          <div className={styles.spinner}></div>
          <div className={styles.spinnerText}>Loading Orders...</div>
        </div>
      ) : error ? (
        <div style={{ color: 'red' }}>Error: {error}</div>
      ) : (

        <div className={styles.ordersTableWrapper}>
          <table className={styles.ordersTable}>
            <thead>
              <tr>
                <th>Order Id</th>
                <th>Customer</th>
                <th>Phone No</th>
                <th>Address</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Tracking Id</th>
                <th>Order Details</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, idx) => (
                <tr key={order.orderNumber}>
                  <td>{order.orderNumber}</td>
                  <td>{order.customerName}</td>
                  <td>{order.customerPhoneNo}</td>
                  <td>{order.address}</td>
                  <td>
                    {order.paymentConfirmed ? (
                      <span className={styles.paymentConfirmed}>Confirmed</span>
                    ) : (
                      <span className={styles.paymentPending}>Pending</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={styles.statusBadge}
                      style={{ background: statusColors[order.status] }}
                    >
                      {statusOptions.find(opt => opt.value === order.status)?.label || order.status}
                    </span>
                  </td>
                  <td className={styles.trackingId}>
                    {order.trackingNumber && order.trackingNumber !== '-' ? order.trackingNumber : '-'}
                  </td>
                  <td style={{ verticalAlign: 'middle', textAlign: 'left', paddingTop: 0, paddingBottom: 0 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewOrder(order)}
                      className={styles.viewOrderButton}
                      startIcon={<VisibilityIcon />}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selectedOrder && (
        <Modal open={!!selectedOrder} onClose={handleCloseModal}>
          <Box className={styles.orderModalBox}>
            <h2>Order Details</h2>
            <div><b>Order Id:</b> {selectedOrder.orderNumber}</div>
            <div><b>Product Id:</b> {selectedOrder.productId}</div>
            <div><b>Product Name:</b> {selectedOrder.productName}</div>
            <div><b>Customer:</b> {selectedOrder.customerName}</div>
            <div><b>Phone No:</b> {selectedOrder.customerPhoneNo}</div>
            <div><b>Address:</b> {selectedOrder.address}</div>
            <div><b>Price:</b> ₹{selectedOrder.price}</div>
            <div><b>Payment:</b> {selectedOrder.paymentConfirmed ? 'Confirmed' : 'Pending'}</div>
            <div><b>Status:</b> {statusOptions.find(opt => opt.value === selectedOrder.status)?.label || selectedOrder.status}</div>
            <div><b>Tracking Id:</b> {selectedOrder.trackingNumber || '-'}</div>
            <Button
              variant="contained"
              onClick={handleCloseModal}
              className={styles.closeOrderButton}
            >
              CLOSE
            </Button>
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default Orders;
